/**
 * @file Submission read-only / review UI components — backgroundDataType.ts.
 */

import type { FormField } from "../../interfaces/formInterfaces";

/** Type definition — Data Type Dropdown Option. */
export type DataTypeDropdownOption = {
  id?: number | null;
  value?: string | number | null;
  label?: string | null;
};

/** Exported function — data Type Field Options. */
export function dataTypeFieldOptions(
  field: Pick<FormField, "dropdown" | "dropdownValues"> | null | undefined
): DataTypeDropdownOption[] {
  return field?.dropdown?.options ?? field?.dropdownValues ?? [];
}

/** Resolve stored slug / label / legacy text to the canonical dropdown value. */
export function normalizeDataTypeValue(
  raw: unknown,
  options: DataTypeDropdownOption[] = []
): string {
  const s = String(raw ?? "").trim();
  if (!s) return "";

  for (const opt of options) {
    const value = String(opt.value ?? "").trim();
    const label = String(opt.label ?? "").trim();
    const id = opt.id != null ? String(opt.id) : "";

    if (value && value === s) return value;
    if (id && id === s && value) return value;
    if (label && label.toLowerCase() === s.toLowerCase()) {
      return value || label;
    }
    if (value && value.toLowerCase() === s.toLowerCase()) return value;
  }

  return s;
}

/** Human-readable label for a stored data type value. */
export function dataTypeLabelForValue(
  raw: unknown,
  options: DataTypeDropdownOption[] = []
): string {
  const s = String(raw ?? "").trim();
  if (!s) return "";

  const normalized = normalizeDataTypeValue(s, options);
  const byValue = options.find(
    (opt) => String(opt.value ?? "").trim() === normalized
  );
  if (byValue?.label) return String(byValue.label);

  const byLabel = options.find(
    (opt) => String(opt.label ?? "").trim().toLowerCase() === s.toLowerCase()
  );
  if (byLabel?.label) return String(byLabel.label);

  return normalized || s;
}
